/**
 * Min/max pair for numeric filters (amount, passenger count).
 *
 * Inverted input (min > max) is NOT rejected here — `parseBookingFilters`
 * normalises it by swapping. Blocking it in the UI would leave the operator
 * staring at a validation error mid-typing, e.g. while changing 100–2000 to
 * 500–1500 the intermediate state is briefly inverted.
 */

import { Input } from '@/components/ui/input'

interface Props {
  readonly label: string
  readonly min: number | undefined
  readonly max: number | undefined
  readonly onChange: (next: { min?: number; max?: number }) => void
  /** Renders a currency affordance and 2dp step. */
  readonly currency?: boolean
  readonly minPlaceholder?: string
  readonly maxPlaceholder?: string
}

export function NumberRangeFilter({
  label,
  min,
  max,
  onChange,
  currency = false,
  minPlaceholder = 'Min',
  maxPlaceholder = 'Max',
}: Props) {
  /** '' clears the bound; anything unparseable is ignored rather than zeroed. */
  const parse = (raw: string): number | undefined => {
    if (raw.trim() === '') return undefined
    const n = Number(raw)
    return Number.isFinite(n) ? n : undefined
  }

  const id = label.toLowerCase().replace(/\s+/g, '-')

  return (
    <div>
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      <div className="mt-1 flex items-center gap-2">
        <div className="relative flex-1">
          {currency && (
            <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
              $
            </span>
          )}
          <Input
            id={`${id}-min`}
            aria-label={`${label} minimum`}
            type="number"
            inputMode="decimal"
            step={currency ? '0.01' : '1'}
            min={0}
            placeholder={minPlaceholder}
            value={min ?? ''}
            onChange={(e) => onChange({ min: parse(e.target.value), max })}
            className={`h-9 text-sm ${currency ? 'pl-6' : ''}`}
          />
        </div>
        <span className="text-muted-foreground">–</span>
        <div className="relative flex-1">
          {currency && (
            <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
              $
            </span>
          )}
          <Input
            id={`${id}-max`}
            aria-label={`${label} maximum`}
            type="number"
            inputMode="decimal"
            step={currency ? '0.01' : '1'}
            min={0}
            placeholder={maxPlaceholder}
            value={max ?? ''}
            onChange={(e) => onChange({ min, max: parse(e.target.value) })}
            className={`h-9 text-sm ${currency ? 'pl-6' : ''}`}
          />
        </div>
      </div>
    </div>
  )
}
